/**
 * Connection form for adding new links between assets
 */
import type React from "react";
import type { SmartAssetData } from "../../types";
import { SOURCE_INTERFACES, TARGET_INTERFACES } from "./types";

export interface WireConnectionFormProps {
  selectedAssets: SmartAssetData[];
  sourceAsset: string;
  setSourceAsset: (id: string) => void;
  sourceInterface: string;
  setSourceInterface: (iface: string) => void;
  targetAsset: string;
  setTargetAsset: (id: string) => void;
  targetInterface: string;
  setTargetInterface: (iface: string) => void;
  isFormValid: boolean;
  validationError: string | null;
  getLinkType: () => "signal" | "value";
  getAssetName: (id: string) => string;
  onAddLink: () => void;
  onClearError: () => void;
}

export const WireConnectionForm: React.FC<WireConnectionFormProps> = ({
  selectedAssets,
  sourceAsset,
  setSourceAsset,
  sourceInterface,
  setSourceInterface,
  targetAsset,
  setTargetAsset,
  targetInterface,
  setTargetInterface,
  isFormValid,
  validationError,
  getLinkType,
  getAssetName,
  onAddLink,
  onClearError,
}) => (
  <div className="mb-6 p-4 bg-primary/10 rounded-lg border border-primary/40">
    <p className="text-sm font-medium text-primary mb-3">Add Connection</p>
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {/* Source Asset */}
      <div>
        <label className="block text-xs font-medium text-sa-text-muted mb-1">Source Asset</label>
        <select
          value={sourceAsset}
          onChange={(e) => {
            setSourceAsset(e.target.value);
            onClearError();
          }}
          className="w-full px-3 py-2 text-sm text-sa-text bg-sa-surface border border-sa-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          data-testid="wire-source-asset"
        >
          <option value="" className="text-sa-text-muted">
            Select asset...
          </option>
          {selectedAssets.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.name}
            </option>
          ))}
        </select>
      </div>

      {/* Source Interface */}
      <div>
        <label className="block text-xs font-medium text-sa-text-muted mb-1">
          Output Interface
        </label>
        <select
          value={sourceInterface}
          onChange={(e) => {
            setSourceInterface(e.target.value);
            onClearError();
          }}
          className="w-full px-3 py-2 text-sm text-sa-text bg-sa-surface border border-sa-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          data-testid="wire-source-interface"
        >
          <option value="" className="text-sa-text-muted">
            Select output...
          </option>
          {SOURCE_INTERFACES.map((iface) => (
            <option key={iface} value={iface}>
              {iface}
            </option>
          ))}
        </select>
      </div>

      {/* Target Asset */}
      <div>
        <label className="block text-xs font-medium text-sa-text-muted mb-1">Target Asset</label>
        <select
          value={targetAsset}
          onChange={(e) => {
            setTargetAsset(e.target.value);
            onClearError();
          }}
          className="w-full px-3 py-2 text-sm text-sa-text bg-sa-surface border border-sa-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          data-testid="wire-target-asset"
        >
          <option value="" className="text-sa-text-muted">
            Select asset...
          </option>
          {selectedAssets
            .filter((a) => a.id !== sourceAsset)
            .map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.name}
              </option>
            ))}
        </select>
      </div>

      {/* Target Interface */}
      <div>
        <label className="block text-xs font-medium text-sa-text-muted mb-1">Input Interface</label>
        <select
          value={targetInterface}
          onChange={(e) => {
            setTargetInterface(e.target.value);
            onClearError();
          }}
          className="w-full px-3 py-2 text-sm text-sa-text bg-sa-surface border border-sa-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
          data-testid="wire-target-interface"
        >
          <option value="" className="text-sa-text-muted">
            Select input...
          </option>
          {TARGET_INTERFACES.map((iface) => (
            <option key={iface} value={iface}>
              {iface}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* Add Button */}
    <div className="mt-3 flex items-center gap-3">
      <button
        onClick={onAddLink}
        disabled={!isFormValid}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          isFormValid
            ? "bg-primary text-white hover:bg-primary-hover"
            : "bg-sa-border text-sa-text-muted cursor-not-allowed"
        }`}
        data-testid="wire-add-link"
      >
        Add Connection
      </button>
      {isFormValid && !validationError && (
        <span className="text-xs text-sa-text-muted">
          {getLinkType()} link: {getAssetName(sourceAsset)}.{sourceInterface} →{" "}
          {getAssetName(targetAsset)}.{targetInterface}
        </span>
      )}
    </div>

    {/* Validation Error Feedback */}
    {validationError && (
      <div
        className="mt-3 flex items-start gap-2 p-3 bg-sa-danger/10 border border-sa-danger/40 rounded-lg"
        role="alert"
        data-testid="wire-validation-error"
      >
        <svg
          className="w-4 h-4 text-sa-danger mt-0.5 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
            clipRule="evenodd"
          />
        </svg>
        <span className="text-sm text-sa-danger">{validationError}</span>
      </div>
    )}
  </div>
);
